import "./style.scss";

import { Avatar, Tooltip } from "@mui/material";
import { PortalType } from "contexts/portal.context";
import format from "date-fns/format";
import { documentsStatusLabel } from "helpers/beneficiaryDocuments";
import { getCleanStartorEndDay, getInitials } from "helpers/utils";
import { forwardRef, useContext } from "react";
import { useTranslation } from "react-i18next";

import variables from "../../../assets/styles/_variables.module.scss";
import LoadingDots from "components/LoadingDots";
import CopyToClipboard from "../CopyToClipboard";
import TextSkeleton from "../TextSkeleton";

interface BeneficiarListItemProps {
    element?: any;
    currentElementId?: number;
    index: number;
    action?: (element: any) => void;
    disabled?: boolean;
    showId?: boolean;
    isLoading?: boolean;
}

const BeneficiarListItem = forwardRef<HTMLDivElement, BeneficiarListItemProps>(function BeneficiarListItem(
    props: BeneficiarListItemProps,
    ref,
) {
    const { element, currentElementId, index, action, disabled = false, showId = false, isLoading = false } = props;
    const publisherType = useContext(PortalType);

    const [t] = useTranslation();

    return (
        <div className="beneficiar-list-item" style={disabled ? { opacity: 0.1, pointerEvents: "none" } : {}} ref={ref}>
            <div
                key={`${element?.id ?? "skeleton"}_${index}`}
                onClick={isLoading || !action || !element ? undefined : () => action(element)}
            >
                <div
                    className={`beneficiars-list-element${
                        !isLoading && element?.id === currentElementId ? " active" : ""
                    }${isLoading ? " beneficiars-list-element--loading" : ""}`}
                >
                    <div className="avatar-block" style={{ display: "flex", alignItems: "stretch", gap: "10px" }}>
                        {isLoading ? (
                            <TextSkeleton variant="circular" width={40} height={40} />
                        ) : (
                            <Avatar
                                src={element.avatarUrl}
                                sx={{
                                    backgroundColor: variables["beneficiary-avatar-background"],
                                    color: variables["beneficiary-avatar-letter"],
                                    fontSize: 14,
                                }}
                                imgProps={{ sx: { objectFit: "cover" } }}
                            >
                                {getInitials(`${element.name ?? ""} ${element.surname ?? ""}`)}
                            </Avatar>
                        )}
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <div className="beneficiars-list-element-tags">
                                {!isLoading &&
                                    element.tags &&
                                    element.tags
                                        .slice(0)
                                        .reverse()
                                        .map((item: any, i: number) => {
                                            return i < 15 ? (
                                                <Tooltip key={`tooltip-${item.id}`} title={item.name} arrow>
                                                    <span
                                                        className={`beneficiars-list-element-tag-${item.color}`}
                                                        key={item.id}
                                                    ></span>
                                                </Tooltip>
                                            ) : null;
                                        })}
                                <span className={`elements-list-element-tag`}></span>
                            </div>
                            <span
                                title={isLoading ? undefined : element.name}
                                className="beneficiar-list-beneficiar-name"
                            >
                                {isLoading ? (
                                    <TextSkeleton width={`${7 + (index % 4)}em`} />
                                ) : (
                                    element.surname.toUpperCase() + " " + (element.name ?? "")
                                )}
                            </span>
                            {!isLoading && element?.deceasedAt && (
                                <span className="beneficiar-list-deceased-chip">{t("Heirs.Deceased")}</span>
                            )}
                            <div className="beneficiar-list-info">
                                {isLoading ? (
                                    <span>
                                        <TextSkeleton width={`${5 + (index % 3)}em`} />
                                    </span>
                                ) : ["audiovisual", "press"].includes(publisherType) ? (
                                    <span>
                                        {element.articlesCount == null ? (
                                            <LoadingDots compact />
                                        ) : (
                                            t(`PressAudio.CountArticles_${publisherType}`, {
                                                count: element.articlesCount,
                                            })
                                        )}
                                    </span>
                                ) : (
                                    <span>
                                        {element.titlesCount == null ? (
                                            <LoadingDots compact />
                                        ) : (
                                            `${element.titlesCount} ${t("Beneficiars.TitlesCount")}`
                                        )}
                                    </span>
                                )}
                                {showId && !isLoading && (
                                    <CopyToClipboard className="beneficiary-id" color="var(--foreground)" label="- ID:">
                                        {element.id}
                                    </CopyToClipboard>
                                )}
                            </div>
                        </div>
                        <div className="beneficiars-list-element-date">
                            {!isLoading && element?.accountStatus === "confirmed" && (
                                <div className="confirmed">
                                    <span className="hovered-status">{`${t("Beneficiars.AccountStatus")} : ${t(
                                        "Beneficiars.AccountConfirmed",
                                    )}`}</span>
                                </div>
                            )}
                            {!isLoading && element?.accountStatus === "pending" && (
                                <div className="pending">
                                    <span className="hovered-status">{`${t("Beneficiars.AccountStatus")} : ${t(
                                        "Beneficiars.AccountPending",
                                    )}`}</span>
                                </div>
                            )}
                            {!isLoading && element?.accountStatus === "noaccount" && (
                                <div className="noaccount">
                                    <span className="hovered-status">{`${t("Beneficiars.AccountStatus")} : ${t(
                                        "Beneficiars.AccountNotFound",
                                    )}`}</span>
                                </div>
                            )}
                            {!isLoading && element?.documentsStatus && (
                                <div className={`documents-${element.documentsStatus.level}`}>
                                    <span className="hovered-status">
                                        {documentsStatusLabel(t, element.documentsStatus)}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="date-block">
                        {!isLoading && element?.releaseDate
                            ? format(getCleanStartorEndDay(element.releaseDate), "d MMM")
                            : ""}
                    </div>
                </div>
            </div>
        </div>
    );
});

export default BeneficiarListItem;
